// =============================================================================
// Task #2040 — "Undo edit" (restore the immediately previous version).
// -----------------------------------------------------------------------------
// Every accepted in-place edit on a saved ad quarantines the superseded R2
// object for the retention window, so the ad can be repointed one step back.
// This module is the shared client surface used by My Ads (ad library) and
// both Ads Express pages:
//   • useUndoableAdIds(brandId) — the set of ad ids with an undoable last
//     edit (server-driven; an ad drops out once the superseded object is
//     purged or after a successful undo/redo pair lands elsewhere).
//   • UndoAdEditButton — a self-gating button: renders nothing when the ad
//     has no restorable previous version, calls the undo endpoint, refreshes
//     the index, and hands the restored durable URL to the caller.
// =============================================================================
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Undo2, Redo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";

export const undoableAdsQueryKey = (brandId: string) =>
  [`/api/brands/${brandId}/generated-ads-undoable`] as const;

export const redoableAdsQueryKey = (brandId: string) =>
  [`/api/brands/${brandId}/generated-ads-redoable`] as const;

// Undo/redo availability always moves together (an undo creates a redo, an
// edit invalidates one, …), so one invalidation refreshes both indexes.
export function invalidateUndoableAds(brandId: string | undefined) {
  if (!brandId) return;
  queryClient.invalidateQueries({ queryKey: undoableAdsQueryKey(brandId) });
  queryClient.invalidateQueries({ queryKey: redoableAdsQueryKey(brandId) });
}

export function useUndoableAdIds(brandId: string | undefined): Set<string> {
  const { data } = useQuery<{ adIds: string[] }>({
    queryKey: undoableAdsQueryKey(brandId ?? ""),
    enabled: !!brandId,
    staleTime: 30_000,
  });
  // A new Set per data identity is fine — data is referentially stable
  // between fetches under React Query.
  return new Set(data?.adIds ?? []);
}

// Ads with a redoable undo (a surviving "undone" version newer than any other
// change). Appears only right after an undo; vanishes once a new edit lands
// or the undone object ages out of retention.
export function useRedoableAdIds(brandId: string | undefined): Set<string> {
  const { data } = useQuery<{ adIds: string[] }>({
    queryKey: redoableAdsQueryKey(brandId ?? ""),
    enabled: !!brandId,
    staleTime: 30_000,
  });
  return new Set(data?.adIds ?? []);
}

interface UndoAdEditButtonProps {
  adId: string;
  brandId: string;
  // Pre-fetched undoable set from useUndoableAdIds (shared per page so the
  // index is fetched once, not per tile). Not in the set = renders nothing.
  undoableAdIds: Set<string>;
  // Called with the restored durable URL after a successful undo, so the
  // caller can swap the displayed image / invalidate its own listings.
  onUndone: (durableUrl: string) => void;
  className?: string;
}

// Shared undo/redo action button internals — same self-gating pattern, same
// response shape, only the endpoint/labels differ.
function useRestoreAction(opts: {
  adId: string;
  brandId: string;
  endpoint: string; // e.g. "undo-edit" | "redo-edit"
  successTitle: string;
  successDescription: string;
  failureTitle: string;
  onDone: (durableUrl: string) => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const { adId, brandId, endpoint, successTitle, successDescription, failureTitle, onDone } = opts;
  const run = useCallback(async () => {
    setBusy(true);
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${adId}/${endpoint}`, {});
      const data = (await res.json()) as { durableUrl?: string };
      invalidateApprovalStates(queryClient, brandId);
      toast({ title: successTitle, description: successDescription });
      if (data?.durableUrl) onDone(data.durableUrl);
    } catch (e: unknown) {
      toast({
        title: failureTitle,
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      // Success or failure, re-derive availability from the server (a 404/410
      // here means the target version is gone — the button should vanish).
      invalidateUndoableAds(brandId);
      setBusy(false);
    }
  }, [adId, brandId, endpoint, successTitle, successDescription, failureTitle, onDone, toast]);
  return { run, busy };
}

export function UndoAdEditButton({
  adId,
  brandId,
  undoableAdIds,
  onUndone,
  className,
}: UndoAdEditButtonProps) {
  const { run, busy } = useRestoreAction({
    adId,
    brandId,
    endpoint: "undo-edit",
    successTitle: "Edit undone",
    successDescription: "The ad was restored to its previous version.",
    failureTitle: "Couldn't undo the edit",
    onDone: onUndone,
  });

  if (!undoableAdIds.has(adId)) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={run}
      disabled={busy}
      className={className ?? "h-8 text-xs"}
      title="Restore the version this ad showed before its last edit"
      data-testid={`button-undo-edit-${adId}`}
    >
      {busy ? (
        <>
          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          Undoing…
        </>
      ) : (
        <>
          <Undo2 className="h-3.5 w-3.5 mr-1" />
          Undo edit
        </>
      )}
    </Button>
  );
}

interface RedoAdEditButtonProps {
  adId: string;
  brandId: string;
  // Pre-fetched redoable set from useRedoableAdIds. Not in the set = renders
  // nothing (no undo has happened, a newer edit landed, or retention passed).
  redoableAdIds: Set<string>;
  onRedone: (durableUrl: string) => void;
  className?: string;
}

// "Redo" — brings back the edit an undo just removed. Deliberately a separate
// button from Undo so Undo never silently toggles (user decision, Task #2040).
export function RedoAdEditButton({
  adId,
  brandId,
  redoableAdIds,
  onRedone,
  className,
}: RedoAdEditButtonProps) {
  const { run, busy } = useRestoreAction({
    adId,
    brandId,
    endpoint: "redo-edit",
    successTitle: "Edit redone",
    successDescription: "The undone edit was brought back.",
    failureTitle: "Couldn't redo the edit",
    onDone: onRedone,
  });

  if (!redoableAdIds.has(adId)) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={run}
      disabled={busy}
      className={className ?? "h-8 text-xs"}
      title="Bring back the edit you just undid"
      data-testid={`button-redo-edit-${adId}`}
    >
      {busy ? (
        <>
          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          Redoing…
        </>
      ) : (
        <>
          <Redo2 className="h-3.5 w-3.5 mr-1" />
          Redo
        </>
      )}
    </Button>
  );
}
